import { useState } from "react"
import { DashboardShell } from "@/components/dashboard-shell"
import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbPage } from "@/components/ui/breadcrumb"
import { Separator } from "@/components/ui/separator"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Search, Sparkles, FileText, TrendingUp, MapPin, Calendar } from "lucide-react"

const dummyResults = [
  {
    id: 1,
    type: "project",
    title: "Highland Wind Farm Phase 2",
    description:
      "Large-scale wind energy project in Scottish Highlands with 150MW capacity. Gate 2 score: 8.7/10. Expected completion Q3 2026.",
    relevance: 0.95,
    gate2Score: 8.7,
    location: "Scottish Highlands",
    technology: "Onshore Wind",
    capacity: "150MW",
    date: "2024-01-15",
  },
  {
    id: 2,
    type: "report",
    title: "Q4 2025 Renewable Energy Market Analysis",
    description:
      "Comprehensive analysis of renewable energy market trends, showing 23% growth in offshore wind investments and emerging opportunities in Scotland.",
    relevance: 0.89,
    category: "Market Analysis",
    date: "2025-12-20",
  },
  {
    id: 3,
    type: "project",
    title: "Thames Estuary Solar Array",
    description:
      "Innovative floating solar installation with 75MW capacity. Gate 2 readiness score: 7.9/10. Strong ESG credentials.",
    relevance: 0.87,
    gate2Score: 7.9,
    location: "Thames Estuary, UK",
    technology: "Solar",
    capacity: "75MW",
    date: "2024-02-10",
  },
  {
    id: 4,
    type: "analysis",
    title: "Regional Investment Opportunities: Scotland",
    description:
      "Deep dive into Scottish renewable energy landscape, highlighting policy support, grid capacity, and development pipeline.",
    relevance: 0.82,
    category: "Regional Analysis",
    date: "2025-11-05",
  },
]

const suggestedQueries = [
  "High Gate 2 score wind projects in Scotland",
  "Solar projects above 50MW capacity",
  "Recent market analysis reports",
  "Technology trends in offshore wind",
]

export default function AISearchPage() {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<typeof dummyResults>([])
  const [isSearching, setIsSearching] = useState(false)

  const handleSearch = () => {
    if (!query.trim()) return

    setIsSearching(true)
    // Simulate API call
    setTimeout(() => {
      setResults(dummyResults)
      setIsSearching(false)
    }, 800)
  }

  return (
    <DashboardShell>
        <header className="flex h-16 shrink-0 items-center gap-2 border-b border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>AI Search</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </header>

        <div className="flex flex-1 flex-col gap-6 p-6">
          {/* Search Header */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-primary" />
              <h1 className="text-3xl font-bold tracking-tight">AI-Powered Search</h1>
            </div>
            <p className="text-muted-foreground">
              Search across projects, reports, and analysis using natural language
            </p>
          </div>

          {/* Search Bar */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Ask anything about renewable energy projects..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="pl-10"
              />
            </div>
            <Button onClick={handleSearch} disabled={isSearching}>
              {isSearching ? "Searching..." : "Search"}
            </Button>
          </div>

          {/* Suggested Queries */}
          {results.length === 0 && (
            <div className="flex flex-col gap-3">
              <p className="text-sm font-medium text-muted-foreground">Suggested queries:</p>
              <div className="flex flex-wrap gap-2">
                {suggestedQueries.map((suggestion, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setQuery(suggestion)
                      setIsSearching(true)
                      setTimeout(() => {
                        setResults(dummyResults)
                        setIsSearching(false)
                      }, 800)
                    }}
                    className="text-xs"
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Search Results */}
          {results.length > 0 && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Found {results.length} results for "{query || suggestedQueries[0]}"
                </p>
                <Button variant="ghost" size="sm" onClick={() => setResults([])}>
                  Clear
                </Button>
              </div>

              <div className="grid gap-4">
                {results.map((result) => (
                  <Card key={result.id} className="cursor-pointer transition-colors hover:border-primary/50">
                    <CardHeader>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {result.type === "project" && <TrendingUp className="h-4 w-4 text-primary" />}
                            {result.type === "report" && <FileText className="h-4 w-4 text-blue-500" />}
                            {result.type === "analysis" && <Sparkles className="h-4 w-4 text-purple-500" />}
                            <Badge variant="outline" className="text-xs">
                              {result.type}
                            </Badge>
                            <Badge variant="secondary" className="text-xs">
                              {Math.round(result.relevance * 100)}% match
                            </Badge>
                          </div>
                          <CardTitle className="text-xl mb-2">{result.title}</CardTitle>
                          <CardDescription>{result.description}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                        {result.type === "project" && (
                          <>
                            <div className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {result.location}
                            </div>
                            <div className="flex items-center gap-1">
                              <TrendingUp className="h-3 w-3" />
                              {result.technology}
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="font-medium">{result.capacity}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="font-medium text-primary">Gate 2: {result.gate2Score}/10</span>
                            </div>
                          </>
                        )}
                        {(result.type === "report" || result.type === "analysis") && (
                          <>
                            <div className="flex items-center gap-1">
                              <FileText className="h-3 w-3" />
                              {result.category}
                            </div>
                          </>
                        )}
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(result.date).toLocaleDateString("en-GB")}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
    </DashboardShell>
  )
}
